// src/components/dashboard/ManageMerchModal.js
// Updated: 10/20/2025 08:55 AM (Added Edit Functionality)

import React, { useState, useEffect } from 'react'; // Added useEffect
import { db } from '../../firebase/config';
// Import doc and updateDoc
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import Spinner from '../common/Spinner';
import { FaPlus, FaTrash } from 'react-icons/fa';
import ModalWrapper from '../common/ModalWrapper';

// Accept existingMerch prop
const ManageMerchModal = ({ familyId, eventId, existingMerch, onClose }) => {
  const [itemName, setItemName] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [sizes, setSizes] = useState([{ id: Date.now(), size: '', upcharge: '' }]); // Use Date.now() for initial ID
  const [loading, setLoading] = useState(false);
  const isEditMode = !!existingMerch; // Determine if we are editing

  // Pre-fill form if editing existing merchandise
  useEffect(() => {
    if (isEditMode && existingMerch) {
      setItemName(existingMerch.itemName || '');
      setBasePrice(existingMerch.basePrice?.toString() || ''); // Ensure basePrice is a string for input
      // Ensure sizes have unique IDs for React keys, map existing data
      setSizes(existingMerch.sizes?.map((s, index) => ({
        id: Date.now() + index, // Generate unique IDs for existing sizes
        size: s.size || '',
        upcharge: s.upcharge?.toString() || '' // Ensure upcharge is a string
      })) || [{ id: Date.now(), size: '', upcharge: '' }]); // Fallback if no sizes exist
    } else {
        // Reset form for adding new item
        setItemName('');
        setBasePrice('');
        setSizes([{ id: Date.now(), size: '', upcharge: '' }]);
    }
  }, [existingMerch, isEditMode]);


  const handleSizeChange = (id, field, value) => setSizes(sizes.map(s => (s.id === id ? { ...s, [field]: value } : s)));
  const addSize = () => setSizes([...sizes, { id: Date.now(), size: '', upcharge: '' }]);
  const removeSize = (id) => {
    // Prevent removing the last size row
    if (sizes.length > 1) {
        setSizes(sizes.filter(s => s.id !== id));
    } else {
        toast.error("You must have at least one size option.");
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      // Format sizes: filter empty, convert upcharge to float, remove temporary ID
      const formattedSizes = sizes
        .filter(s => s.size.trim() !== '')
        .map(({ id, ...rest }) => ({
            ...rest,
            upcharge: parseFloat(rest.upcharge) || 0,
         }));

      if (formattedSizes.length === 0) {
        toast.error("Please add at least one valid size.");
        setLoading(false);
        return;
      }

      const merchData = {
          itemName,
          basePrice: parseFloat(basePrice) || 0,
          sizes: formattedSizes,
          // Keep createdAt if editing, set new if adding
          createdAt: isEditMode ? existingMerch.createdAt : serverTimestamp(),
          // Add/update updatedAt timestamp
          updatedAt: serverTimestamp()
      };

      if (isEditMode) {
        // Update existing document
        const merchDocRef = doc(db, `families/${familyId}/events/${eventId}/merchandise/${existingMerch.id}`);
        await updateDoc(merchDocRef, merchData);
        toast.success('Merchandise item updated!');
      } else {
        // Add new document
        await addDoc(collection(db, `families/${familyId}/events/${eventId}/merchandise`), merchData);
        toast.success('Merchandise item added!');
      }
      onClose(); // Close modal on success
    } catch (error) {
      console.error("Error saving merchandise: ", error);
      toast.error(`Failed to ${isEditMode ? 'update' : 'add'} merchandise.`);
    }
    finally { setLoading(false); }
  };

  return (
    // ModalWrapper size remains 2xl which is good for this form
    <ModalWrapper onClose={onClose} size="2xl">
      {/* Update title based on mode */}
      <div className="p-6 border-b"><h3 className="text-xl font-semibold text-neutral-gray">{isEditMode ? 'Edit Merchandise' : 'Add Merchandise'}</h3></div>
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Item Name and Base Price inputs remain the same */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label htmlFor="itemName" className="block text-sm font-medium text-gray-700">Item Name</label><input type="text" id="itemName" value={itemName} onChange={(e) => setItemName(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
            <div><label htmlFor="basePrice" className="block text-sm font-medium text-gray-700">Base Price</label><input type="number" step="0.01" id="basePrice" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/></div>
          </div>
          {/* Sizes section remains structurally similar */}
          <div className="pt-4"><h4 className="text-md font-medium text-gray-700 mb-2">Sizes & Pricing</h4>
            <div className="space-y-3">{sizes.map((s) => (<div key={s.id} className="flex items-center gap-2">
                <input type="text" placeholder="Size (e.g., M)" value={s.size} onChange={(e) => handleSizeChange(s.id, 'size', e.target.value)} required className="w-full border-gray-300 rounded-md shadow-sm"/>
                <input type="number" step="0.01" placeholder="Upcharge (e.g., 2.00)" value={s.upcharge} onChange={(e) => handleSizeChange(s.id, 'upcharge', e.target.value)} className="w-full border-gray-300 rounded-md shadow-sm"/>
                {/* Conditionally render trash icon */}
                {sizes.length > 1 && (
                    <button type="button" onClick={() => removeSize(s.id)} className="text-red-500 hover:text-red-700 p-2"><FaTrash /></button>
                )}
                {/* Placeholder for alignment if only one size exists */}
                 {sizes.length <= 1 && (
                    <span className="p-2 w-[36px]"></span> // Match button width roughly
                )}
            </div>))}</div>
            <button type="button" onClick={addSize} className="mt-3 flex items-center gap-2 text-sm text-primary font-semibold hover:underline"><FaPlus size={12} /> Add Size</button>
          </div>
        </div>
        <div className="p-6 bg-gray-50 rounded-b-lg flex justify-end items-center gap-4">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Cancel</button>
          {/* Update button text based on mode */}
          <button type="submit" disabled={loading} className="px-4 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex items-center">{loading ? <Spinner size="sm" /> : isEditMode ? 'Save Changes' : 'Save Merchandise'}</button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default ManageMerchModal;